/**
 * Module-level owner of the Spreedly iFrame handlers.
 *
 * Spreedly's `on` only ever accumulates handlers and `removeHandlers()` strips ALL of them —
 * including the 3DS `3ds:status` listener that useSpreedlyThreeDs binds once. So the card form
 * cannot re-register per mount: the customer toggling Cash → Card → Cash → Card would stack a
 * fresh set of handlers each time, with the older sets calling setState on unmounted instances.
 *
 * Instead the four handlers are registered exactly once and route to whichever form is `live`.
 * `init` is documented as the first call and needs the number/cvv containers in the DOM, so it
 * runs on the first mount only; every later mount calls `reload()`, which re-initializes the
 * iFrames into the freshly rendered containers and fires `ready` again.
 *
 * @see https://developer.spreedly.com/docs/iframe-events
 */

export interface SpreedlyFieldError {
  attribute: string;
  key: string;
  message: string;
}

/** The two fields Spreedly hosts; everything else on the card form is ours. */
export type SpreedlyField = 'number' | 'cvv';

export type SpreedlyFieldEventType =
  | 'focus'
  | 'blur'
  | 'mouseover'
  | 'mouseout'
  | 'input'
  | 'enter'
  | 'escape'
  | 'tab'
  | 'shiftTab';

/**
 * The state map Spreedly attaches to `input` field events (and to the `validation` event). Absent
 * on every other event type. `validNumber` covers format + Luhn + a brand length, which is what
 * the form reads; `luhnValid` (the checksum alone) is in the payload the number frame builds
 * (number-frame-1.191.min.js, 2026-09-15) but not in the documented one, so it stays optional and
 * unused. `iin` is the first 6 digits (8 for Visa/Mastercard).
 */
export interface SpreedlyInputProperties {
  cardType: string;
  validNumber: boolean;
  validCvv: boolean;
  numberLength: number;
  cvvLength: number;
  iin: string;
  luhnValid?: boolean;
}

export interface SpreedlyIframeListeners {
  onReady: () => void;
  onPaymentMethod: (token: string, paymentMethod: unknown) => void;
  onErrors: (errors: SpreedlyFieldError[]) => void;
  onFieldEvent?: (
    name: SpreedlyField,
    type: SpreedlyFieldEventType,
    activeEl: SpreedlyField,
    inputProperties?: SpreedlyInputProperties,
  ) => void;
}

export interface SpreedlyIframeElements {
  numberEl: string;
  cvvEl: string;
}

export interface SpreedlyIframeSdk {
  init: (environmentKey: string, options: SpreedlyIframeElements) => void;
  on: (event: string, callback: (...args: any[]) => void) => void;
  reload: () => void;
}

let initialized = false;
let live: SpreedlyIframeListeners | null = null;

export function attachCardForm(
  spreedly: SpreedlyIframeSdk,
  environmentKey: string,
  elements: SpreedlyIframeElements,
  listeners: SpreedlyIframeListeners,
): void {
  live = listeners;

  if (initialized) {
    spreedly.reload();

    return;
  }

  initialized = true;

  spreedly.init(environmentKey, elements);

  spreedly.on('ready', () => live?.onReady());
  spreedly.on('paymentMethod', (token: string, paymentMethod: unknown) => live?.onPaymentMethod(token, paymentMethod));
  spreedly.on('errors', (errors: SpreedlyFieldError[]) => live?.onErrors(errors ?? []));
  spreedly.on(
    'fieldEvent',
    (
      name: SpreedlyField,
      type: SpreedlyFieldEventType,
      activeEl: SpreedlyField,
      inputProperties?: SpreedlyInputProperties,
    ) => live?.onFieldEvent?.(name, type, activeEl, inputProperties),
  );
}

/** Stops routing events to `listeners`; a no-op when another form has already taken over. */
export function detachCardForm(listeners: SpreedlyIframeListeners): void {
  if (live === listeners) {
    live = null;
  }
}

export function resetSpreedlyIframeForTests(): void {
  initialized = false;
  live = null;
}
